<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { useConfig } from 'dashboard/composables/useConfig';
import StageColorPicker from './StageColorPicker.vue';
import StageIconPicker from './StageIconPicker.vue';
import LabelStageMappings from './LabelStageMappings.vue';
import draggable from 'vuedraggable';
import agents from '../../../../api/agents';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import logger from 'shared/helpers/logger';

const props = defineProps({
  isEditing: {
    type: Boolean,
    default: false,
  },
  initialData: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits(['saved', 'close']);
const { t } = useI18n();
const store = useStore();
useConfig();
const loading = ref(false);
const errorMessage = ref('');
const isDirty = ref(false);

const availableInboxes = computed(() => store.getters['inboxes/getInboxes']);

const formData = ref({
  name: props.initialData.name || '',
  description: props.initialData.description || '',
  inbox_id: props.initialData.inbox_id || null,
  stages: {},
  agents: props.initialData.agents || [],
});

// Toggle para etapas de ganho/perdido (padrão: habilitado)
const enableWonLostStages = ref(true);

// Fase 2.1 — SLA por etapa (default desabilitado por pipeline)
const stageSlaEnabled = ref(false);

// Auto-archive pipeline card when linked conversation is resolved (default OFF).
// When OFF the card stays on the board after the conversation is resolved.
const autoArchiveOnResolved = ref(false);

// Auto-mark card as Won when linked conversation is resolved (default OFF).
// Only effective when "Etapas Ganho/Perdido" is enabled and a Won stage exists.
const autoWinOnResolved = ref(false);

// Label-Stage mappings state
const labelStageMappings = ref({
  label_stage_mappings: [],
  stage_label_mappings: [],
});

// Show/hide label mappings section
const showLabelMappings = ref(false);

// Label mappings validation state
const labelMappingsValid = ref(true);

const stages = ref([]);
const newStage = ref({
  name: '',
  color: '#FF6B6B',
  description: '',
  icon: 'arrow-right',
});

const editingStage = ref(null);

const dragOptions = {
  animation: 150,
  ghostClass: 'ghost-card',
};

const agentsList = ref([]);
const loadingAgents = ref(false);

// Gera o ID baseado no nome (formato temporário - backend normaliza para {pipeline_id}_{slug})
const generateId = name => {
  return name
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
};

// Atualiza o ID apenas para novas etapas
watch(
  () => newStage.value.name,
  newName => {
    if (newName && !editingStage.value) {
      newStage.value.id = generateId(newName);
    }
  }
);

// Converte as etapas para o formato esperado pela API
const formatStagesForAPI = () => {
  const formattedStages = {};

  // Garantir que as posições reflitam a ordem atual
  stages.value.forEach((stage, index) => {
    // Definir explicitamente a posição baseada no índice atual
    const position = index + 1;

    // Pega os templates existentes da etapa atual
    const existingTemplates =
      props.initialData?.stages?.[stage.id]?.message_templates || [];

    formattedStages[stage.id] = {
      name: stage.name,
      color: stage.color,
      position: position, // Garantir que a posição reflita a ordem atual
      description: stage.description,
      icon: stage.icon || 'arrow-right', // Ícone do estágio (padrão: arrow-right)
      message_templates: existingTemplates, // Mantém os templates existentes
    };

    // Preservar flags de ganho/perdido
    if (stage.is_won_stage) {
      formattedStages[stage.id].is_won_stage = true;
    }
    if (stage.is_lost_stage) {
      formattedStages[stage.id].is_lost_stage = true;
    }

    // Fase 2.1 — SLA por etapa (só persiste se valor positivo definido)
    const slaHours = Number.parseInt(stage.sla_hours, 10);
    if (Number.isFinite(slaHours) && slaHours > 0) {
      formattedStages[stage.id].sla_hours = slaHours;
    }

    // Debug da posição definida
    logger.debug(`Formatando para API: ${stage.name} -> posição ${position}`);
  });

  return formattedStages;
};

// Função para lidar com a reordenação das etapas
const handleOrderChange = async () => {
  if (!props.isEditing) return;

  try {
    // Recalcular posições com base na nova ordem após drag-and-drop (B62)
    stages.value.forEach((stage, index) => {
      stage.position = index + 1;
      logger.debug(
        `Atualizando posição: ${stage.name} -> posição ${stage.position}`
      );
    });

    // Marcar form como dirty para exibir indicador visual (B62)
    isDirty.value = true;

    logger.debug('Stages reordered:', formatStagesForAPI());
  } catch (error) {
    logger.error('Erro ao atualizar ordem:', error);
  }
};

const removeStage = index => {
  stages.value.splice(index, 1);
};

// Handler for label-stage mappings update
const onLabelMappingsUpdate = mappings => {
  labelStageMappings.value = mappings;
};

// Handler for label-stage mappings validation
const onLabelMappingsValidUpdate = valid => {
  labelMappingsValid.value = valid;
};

const fetchAgents = async () => {
  try {
    loadingAgents.value = true;
    const { data } = await agents.get();
    // Se tiver agentes pré-selecionados e eles não estiverem na lista, adiciona-os
    if (props.initialData?.agents?.length) {
      props.initialData.agents.forEach(agent => {
        if (!data.find(a => a.id === agent.id)) {
          data.push(agent);
        }
      });
    }
    agentsList.value = data;
  } catch (error) {
    logger.error('Erro ao carregar agentes:', error);
  } finally {
    loadingAgents.value = false;
  }
};

// Inicializa as etapas se estiver editando
onMounted(() => {
  logger.debug('PipelineForm mounted, initialData:', props.initialData);

  if (props.isEditing || props.initialData) {
    logger.debug('Inicializando form com dados:', props.initialData);

    formData.value = {
      name: props.initialData.name || '',
      description: props.initialData.description || '',
      inbox_id: props.initialData.inbox_id || null,
      stages: props.initialData.stages || {},
      agents: props.initialData.agents || [],
    };

    logger.debug('formData atualizado:', formData.value);

    // Inicializa as etapas do template
    if (props.initialData.stages) {
      logger.debug('Inicializando etapas:', props.initialData.stages);

      stages.value = Object.entries(props.initialData.stages)
        .map(([id, stage]) => {
          const mappedStage = {
            id,
            name: stage.name,
            color: stage.color,
            description: stage.description,
            icon: stage.icon || 'arrow-right',
            position: parseInt(stage.position, 10) || 0,
            // Preservar flags de ganho/perdido
            is_won_stage: stage.is_won_stage || false,
            is_lost_stage: stage.is_lost_stage || false,
            // Fase 2.1 — horas de SLA por etapa (JSONB stages[id].sla_hours)
            sla_hours:
              stage.sla_hours != null && Number(stage.sla_hours) > 0
                ? Number(stage.sla_hours)
                : null,
          };
          logger.debug('Etapa mapeada:', mappedStage);
          return mappedStage;
        })
        .sort((a, b) => a.position - b.position);

      logger.debug(
        'Etapas inicializadas e ordenadas por posição:',
        stages.value
      );
    }

    // Inicializa o toggle de ganho/perdido
    if (props.initialData?.settings?.enable_won_lost_stages !== undefined) {
      enableWonLostStages.value =
        props.initialData.settings.enable_won_lost_stages;
    } else {
      enableWonLostStages.value = true; // Padrão: habilitado
    }

    // Fase 2.1 — Inicializa toggle de SLA por etapa
    stageSlaEnabled.value = Boolean(props.initialData?.stage_sla_enabled);

    // Toggles opt-in para automações ao resolver a conversa
    autoArchiveOnResolved.value = Boolean(
      props.initialData?.settings?.auto_archive_on_resolved
    );
    autoWinOnResolved.value = Boolean(
      props.initialData?.settings?.auto_win_on_resolved
    );

    // Initialize label-stage mappings
    if (props.initialData?.settings) {
      labelStageMappings.value = {
        label_stage_mappings:
          props.initialData.settings.label_stage_mappings || [],
        stage_label_mappings:
          props.initialData.settings.stage_label_mappings || [],
      };
      // Show section if there are any mappings
      showLabelMappings.value =
        labelStageMappings.value.label_stage_mappings.length > 0 ||
        labelStageMappings.value.stage_label_mappings.length > 0;
    }
  }

  fetchAgents();
});

// Expanded stage for accordion
const expandedStageId = ref(null);

const handleSubmit = async () => {
  try {
    loading.value = true;
    errorMessage.value = '';

    // Validate stage names before sending to API
    const emptyStage = stages.value.find(s => !s.name?.trim());
    if (emptyStage) {
      expandedStageId.value = emptyStage.id;
      errorMessage.value = t('PIPELINE.PIPELINES.FORM.STAGES.NAME_REQUIRED');
      return;
    }

    // Preparar as etapas mantendo os templates existentes e atualizando posições
    const formattedStages = formatStagesForAPI();

    logger.debug('Estágios formatados para submit:', formattedStages);

    const payload = {
      name: formData.value.name,
      description: formData.value.description,
      inbox_id: formData.value.inbox_id,
      stage_sla_enabled: stageSlaEnabled.value,
      stages: formattedStages,
      agents: formData.value.agents,
      settings: {
        ...(props.initialData?.settings || {}),
        enable_won_lost_stages: enableWonLostStages.value,
        label_stage_mappings: labelStageMappings.value.label_stage_mappings,
        stage_label_mappings: labelStageMappings.value.stage_label_mappings,
        auto_archive_on_resolved: autoArchiveOnResolved.value,
        auto_win_on_resolved: autoWinOnResolved.value,
      },
    };

    let savedPipeline;
    if (props.isEditing) {
      savedPipeline = await store.dispatch('pipelines/updatePipeline', {
        id: props.initialData.id,
        ...payload,
      });
    } else {
      savedPipeline = await store.dispatch('pipelines/createPipeline', payload);
    }

    if (savedPipeline) {
      isDirty.value = false;
      emit('saved', savedPipeline);
    }
  } catch (error) {
    logger.error('Erro ao salvar funil:', error);
    if (error.response?.data?.code === 'PIPELINE_LIMIT_REACHED') {
      errorMessage.value = error.response.data.error;
    } else if (error.response?.data?.errors) {
      // Parse Rails validation errors from the API response
      const apiErrors = error.response.data.errors;
      const messages = Object.entries(apiErrors)
        .flatMap(([field, msgs]) =>
          (Array.isArray(msgs) ? msgs : [msgs]).map(
            msg => `${field === 'base' ? '' : `${field}: `}${msg}`
          )
        )
        .join('; ');
      errorMessage.value =
        messages || t('PIPELINE.PIPELINES.FORM.ERROR_GENERIC');
    } else {
      errorMessage.value =
        error.response?.data?.error ||
        error.message ||
        t('PIPELINE.PIPELINES.FORM.ERROR_GENERIC');
    }
  } finally {
    loading.value = false;
  }
};

const toggleStageExpand = stageId => {
  if (expandedStageId.value === stageId) {
    expandedStageId.value = null;
  } else {
    expandedStageId.value = stageId;
  }
};

// Search agents
const agentSearch = ref('');
const filteredAgents = computed(() => {
  if (!agentSearch.value) return agentsList.value;
  const search = agentSearch.value.toLowerCase();
  return agentsList.value.filter(agent =>
    agent.name.toLowerCase().includes(search)
  );
});

// Add new stage (for accordion style)
// Gera ID temporário - o backend normaliza para {pipeline_id}_{slug} após salvar
const addNewStage = () => {
  const tempId = `new_stage_${Date.now()}`;
  stages.value.push({
    id: tempId,
    name: '',
    color: '#6366f1',
    description: '',
    icon: 'arrow-right',
  });
  expandedStageId.value = tempId;
};

// Remove agent from selected
const removeAgent = agentId => {
  const index = formData.value.agents.findIndex(a => a.id === agentId);
  if (index !== -1) {
    formData.value.agents.splice(index, 1);
  }
};

// Adicione um watch para debug
watch(
  () => formData.value.agents,
  newAgents => {
    logger.debug('Agentes atualizados:', newAgents);
  }
);

const toggleAgent = agent => {
  const index = formData.value.agents.findIndex(a => a.id === agent.id);
  if (index === -1) {
    formData.value.agents.push(agent);
  } else {
    formData.value.agents.splice(index, 1);
  }
};

// Adicionar watch para monitorar mudanças
watch(
  () => stages.value,
  newStages => {
    logger.debug('Etapas atualizadas:', newStages);
  },
  { deep: true }
);

watch(
  () => formData.value,
  newFormData => {
    logger.debug('FormData atualizado:', newFormData);
  },
  { deep: true }
);
</script>

<template>
  <form @submit.prevent="handleSubmit">
    <!-- Mensagem de erro -->
    <div
      v-if="errorMessage"
      class="mb-4 p-3 bg-n-ruby-3 border border-n-ruby-9 rounded-lg"
    >
      <div class="flex items-center gap-2">
        <fluent-icon icon="error-circle" size="16" class="text-n-ruby-11" />
        <span class="text-sm text-n-ruby-11">{{ errorMessage }}</span>
      </div>
    </div>

    <!-- Main Grid: 2 + 3 columns -->
    <div class="grid grid-cols-1 md:grid-cols-5 gap-6">
      <!-- Coluna Esquerda (2/5) -->
      <div class="md:col-span-2 space-y-4">
        <!-- Card: Nome, Descrição e Inbox -->
        <div
          class="bg-n-solid-2 p-4 rounded-xl outline outline-1 outline-n-weak"
        >
          <div class="space-y-4">
            <div>
              <label class="block text-label text-n-slate-12 mb-1">
                {{ t('PIPELINE.PIPELINES.FORM.NAME.LABEL') }}
              </label>
              <input
                v-model="formData.name"
                type="text"
                class="h-10 w-full"
                :placeholder="t('PIPELINE.PIPELINES.FORM.NAME.PLACEHOLDER')"
                required
              />
            </div>
            <div>
              <label class="block text-label text-n-slate-12 mb-1">
                {{ t('PIPELINE.PIPELINES.FORM.DESCRIPTION.LABEL') }}
              </label>
              <textarea
                v-model="formData.description"
                rows="3"
                class="w-full px-3 py-2 border border-n-weak rounded-lg text-sm bg-n-solid-2 text-n-slate-12 resize-none focus:border-n-brand focus:ring-1 focus:ring-n-brand/20"
                :placeholder="
                  t('PIPELINE.PIPELINES.FORM.DESCRIPTION.PLACEHOLDER')
                "
              />
            </div>
            <div>
              <label class="block text-label text-n-slate-12 mb-1">
                {{
                  t('PIPELINE.PIPELINES.FORM.INBOX_INTEGRATION.SELECT_INBOX')
                }}
                <span class="text-n-slate-10 font-normal text-xs"
                  >({{
                    t('PIPELINE.PIPELINES.FORM.INBOX_INTEGRATION.OPTIONAL')
                  }})</span
                >
              </label>
              <select v-model="formData.inbox_id" class="h-10 w-full">
                <option :value="null">
                  {{ t('PIPELINE.PIPELINES.FORM.INBOX_INTEGRATION.NO_INBOX') }}
                </option>
                <option
                  v-for="inbox in availableInboxes"
                  :key="inbox.id"
                  :value="inbox.id"
                >
                  {{ inbox.name }}
                </option>
              </select>
              <p class="mt-1.5 text-xs text-n-slate-11">
                {{ t('PIPELINE.PIPELINES.FORM.INBOX_INTEGRATION.DESCRIPTION') }}
              </p>
              <div
                v-if="formData.inbox_id"
                class="mt-2 p-2.5 bg-n-blue-3 border border-n-blue-9 rounded-lg"
              >
                <div class="flex items-start gap-2">
                  <fluent-icon
                    icon="info"
                    size="14"
                    class="text-n-blue-11 mt-0.5 flex-shrink-0"
                  />
                  <span class="text-xs text-n-blue-11">
                    {{
                      t(
                        'PIPELINE.PIPELINES.FORM.INBOX_INTEGRATION.AUTO_CREATE_INFO'
                      )
                    }}
                  </span>
                </div>
              </div>
            </div>

            <!-- Toggle: Etapas Ganho/Perdido -->
            <div class="p-3 bg-n-alpha-1 border border-n-weak rounded-lg">
              <label class="flex items-center justify-between cursor-pointer">
                <div>
                  <span class="text-sm font-medium text-n-slate-12">
                    {{ t('PIPELINE.PIPELINES.FORM.WON_LOST_STAGES.LABEL') }}
                  </span>
                  <p class="text-xs text-n-slate-11 mt-0.5">
                    {{
                      t('PIPELINE.PIPELINES.FORM.WON_LOST_STAGES.DESCRIPTION')
                    }}
                  </p>
                </div>
                <div class="relative ml-3 flex-shrink-0">
                  <input
                    v-model="enableWonLostStages"
                    type="checkbox"
                    class="sr-only peer"
                  />
                  <div
                    class="w-11 h-6 bg-n-alpha-2 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:border-n-weak after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-n-brand"
                  />
                </div>
              </label>
            </div>

            <!-- Toggle: SLA por etapa (Fase 2.1) -->
            <div class="p-3 bg-n-alpha-1 border border-n-weak rounded-lg">
              <label class="flex items-center justify-between cursor-pointer">
                <div>
                  <span class="text-sm font-medium text-n-slate-12">
                    {{ t('PIPELINE.SLA.SETTINGS_ENABLE') }}
                  </span>
                  <p class="text-xs text-n-slate-11 mt-0.5">
                    {{ t('PIPELINE.SLA.SETTINGS_HELP') }}
                  </p>
                </div>
                <div class="relative ml-3 flex-shrink-0">
                  <input
                    v-model="stageSlaEnabled"
                    type="checkbox"
                    class="sr-only peer"
                  />
                  <div
                    class="w-11 h-6 bg-n-alpha-2 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:border-n-weak after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-n-brand"
                  />
                </div>
              </label>
            </div>

            <!-- Toggle: Arquivar card ao resolver a conversa -->
            <div class="p-3 bg-n-alpha-1 border border-n-weak rounded-lg">
              <label class="flex items-center justify-between cursor-pointer">
                <div>
                  <span class="text-sm font-medium text-n-slate-12">
                    {{
                      t(
                        'PIPELINE.PIPELINES.FORM.AUTO_ARCHIVE_ON_RESOLVED.LABEL'
                      )
                    }}
                  </span>
                  <p class="text-xs text-n-slate-11 mt-0.5">
                    {{
                      t(
                        'PIPELINE.PIPELINES.FORM.AUTO_ARCHIVE_ON_RESOLVED.DESCRIPTION'
                      )
                    }}
                  </p>
                </div>
                <div class="relative ml-3 flex-shrink-0">
                  <input
                    v-model="autoArchiveOnResolved"
                    type="checkbox"
                    class="sr-only peer"
                  />
                  <div
                    class="w-11 h-6 bg-n-alpha-2 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:border-n-weak after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-n-brand"
                  />
                </div>
              </label>
            </div>

            <!-- Toggle: Marcar como Ganho ao resolver a conversa (requer Ganho/Perdido) -->
            <div
              v-if="enableWonLostStages"
              class="p-3 bg-n-alpha-1 border border-n-weak rounded-lg"
            >
              <label class="flex items-center justify-between cursor-pointer">
                <div>
                  <span class="text-sm font-medium text-n-slate-12">
                    {{
                      t('PIPELINE.PIPELINES.FORM.AUTO_WIN_ON_RESOLVED.LABEL')
                    }}
                  </span>
                  <p class="text-xs text-n-slate-11 mt-0.5">
                    {{
                      t(
                        'PIPELINE.PIPELINES.FORM.AUTO_WIN_ON_RESOLVED.DESCRIPTION'
                      )
                    }}
                  </p>
                </div>
                <div class="relative ml-3 flex-shrink-0">
                  <input
                    v-model="autoWinOnResolved"
                    type="checkbox"
                    class="sr-only peer"
                  />
                  <div
                    class="w-11 h-6 bg-n-alpha-2 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:border-n-weak after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-n-brand"
                  />
                </div>
              </label>
            </div>
          </div>
        </div>

        <!-- Card: Agentes do Funil -->
        <div
          class="bg-n-solid-2 p-4 rounded-xl outline outline-1 outline-n-weak"
        >
          <h2 class="text-heading-3 text-n-slate-12 mb-4">
            {{ t('PIPELINE.PIPELINES.FORM.AGENTS.TITLE') }}
          </h2>

          <!-- Search -->
          <div class="relative mb-4">
            <fluent-icon
              icon="search"
              size="16"
              class="absolute left-3 top-1/2 -translate-y-1/2 text-n-slate-10"
            />
            <input
              v-model="agentSearch"
              type="text"
              class="w-full pl-10 pr-4 py-2 border border-n-weak rounded-lg text-sm bg-n-solid-2 text-n-slate-12 focus:border-n-brand focus:ring-1 focus:ring-n-brand/20"
              :placeholder="
                t('PIPELINE.PIPELINES.FORM.AGENTS.SEARCH_PLACEHOLDER')
              "
            />
          </div>

          <!-- Loading State -->
          <div
            v-if="loadingAgents"
            class="flex items-center justify-center py-6"
          >
            <span
              class="w-6 h-6 border-2 border-n-weak border-t-n-brand rounded-full animate-spin"
            />
          </div>

          <!-- Selected Agents List -->
          <div v-else-if="formData.agents.length" class="space-y-2 mb-4">
            <div
              v-for="agent in formData.agents"
              :key="agent.id"
              class="flex items-center gap-3 p-2 bg-n-alpha-1 rounded-lg"
            >
              <Avatar
                :name="agent.name"
                :src="agent.avatar_url"
                :size="32"
                :status="agent.availability_status"
              />
              <span class="flex-grow font-medium text-sm text-n-slate-12">
                {{ agent.name }}
              </span>
              <button
                type="button"
                class="text-n-ruby-11 hover:text-n-ruby-11 transition-colors"
                @click="removeAgent(agent.id)"
              >
                <fluent-icon icon="delete" size="16" />
              </button>
            </div>
          </div>

          <!-- Available Agents to Add -->
          <div
            v-if="filteredAgents.length && !loadingAgents"
            class="space-y-2 max-h-[200px] overflow-y-auto"
          >
            <div
              v-for="agent in filteredAgents.filter(
                a => !formData.agents.some(s => s.id === a.id)
              )"
              :key="agent.id"
              class="flex items-center gap-3 p-2 hover:bg-n-alpha-1 rounded-lg cursor-pointer transition-colors"
              @click="toggleAgent(agent)"
            >
              <Avatar
                :name="agent.name"
                :src="agent.avatar_url"
                :size="32"
                :status="agent.availability_status"
              />
              <span class="flex-grow font-medium text-sm text-n-slate-12">
                {{ agent.name }}
              </span>
              <fluent-icon icon="add" size="16" class="text-n-brand" />
            </div>
          </div>

          <!-- Empty State -->
          <div
            v-if="!loadingAgents && !agentsList.length"
            class="flex items-center justify-center py-6 text-n-slate-10"
          >
            <fluent-icon icon="people" size="20" class="mr-2" />
            <span class="text-sm">{{
              t('PIPELINE.PIPELINES.FORM.AGENTS.EMPTY')
            }}</span>
          </div>
        </div>

        <!-- Card: Automações de Labels -->
        <div
          class="bg-n-solid-2 p-4 rounded-xl outline outline-1 outline-n-weak"
        >
          <button
            type="button"
            class="w-full flex items-center justify-between"
            @click="showLabelMappings = !showLabelMappings"
          >
            <div class="flex items-center gap-2">
              <fluent-icon icon="tag" size="18" class="text-n-brand" />
              <div class="text-left">
                <h2 class="text-heading-3 text-n-slate-12">
                  {{ t('PIPELINE.LABEL_MAPPINGS.TITLE') }}
                </h2>
                <p class="text-xs text-n-slate-11">
                  {{ t('PIPELINE.LABEL_MAPPINGS.SUBTITLE') }}
                </p>
              </div>
            </div>
            <fluent-icon
              :icon="showLabelMappings ? 'chevron-up' : 'chevron-down'"
              size="16"
              class="text-n-slate-10"
            />
          </button>

          <!-- Expandable content -->
          <div
            v-if="showLabelMappings"
            class="mt-4 pt-4 border-t border-n-weak"
          >
            <LabelStageMappings
              :pipeline="initialData"
              :stages="stages"
              @update:mappings="onLabelMappingsUpdate"
              @update:valid="onLabelMappingsValidUpdate"
            />
          </div>
        </div>
      </div>

      <!-- Coluna Direita (3/5) - Etapas -->
      <div
        class="md:col-span-3 bg-n-solid-2 p-4 rounded-xl outline outline-1 outline-n-weak"
      >
        <div class="flex items-center justify-between mb-4">
          <h2 class="text-heading-3 text-n-slate-12">
            {{ t('PIPELINE.PIPELINES.FORM.STAGES.TITLE') }}
          </h2>
          <!-- Badge "Alterações não salvas" (B62) — aparece após drag-and-drop -->
          <span
            v-if="isDirty"
            class="inline-flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-full bg-n-amber-3 text-n-amber-11 border border-n-amber-6"
          >
            {{ t('PIPELINE.PIPELINES.FORM.STAGES.UNSAVED_CHANGES') }}
          </span>
        </div>

        <!-- Lista de Etapas (Accordion) -->
        <div class="space-y-2">
          <draggable
            v-model="stages"
            :options="dragOptions"
            item-key="id"
            handle=".drag-handle"
            class="space-y-2"
            @change="handleOrderChange"
          >
            <template #item="{ element: stage, index }">
              <div
                v-if="
                  enableWonLostStages ||
                  (!stage.is_won_stage && !stage.is_lost_stage)
                "
                class="border rounded-lg bg-n-solid-2"
                :class="{
                  'border-2 border-n-brand': expandedStageId === stage.id,
                  'border-n-weak': expandedStageId !== stage.id,
                }"
              >
                <!-- Stage Header (collapsed) -->
                <div
                  class="p-3 flex items-center gap-3 cursor-pointer"
                  :class="{
                    'border-b border-n-weak': expandedStageId === stage.id,
                  }"
                  @click="toggleStageExpand(stage.id)"
                >
                  <fluent-icon
                    icon="navigation"
                    size="16"
                    class="drag-handle text-n-slate-10 cursor-grab active:cursor-grabbing hover:text-n-slate-12"
                    @click.stop
                  />
                  <div
                    class="w-4 h-4 rounded-full flex-shrink-0"
                    :style="{ backgroundColor: stage.color }"
                  />
                  <span
                    class="font-medium text-sm text-n-slate-12 flex-grow truncate"
                  >
                    {{ stage.name || 'Nova etapa' }}
                  </span>
                  <button
                    type="button"
                    class="text-n-slate-10 hover:text-n-slate-12"
                  >
                    <fluent-icon
                      :icon="
                        expandedStageId === stage.id
                          ? 'chevron-up'
                          : 'chevron-down'
                      "
                      size="16"
                    />
                  </button>
                </div>

                <!-- Stage Form (expanded) -->
                <div v-if="expandedStageId === stage.id" class="p-4 space-y-4">
                  <div>
                    <label class="block text-label text-n-slate-12 mb-1">
                      {{ t('PIPELINE.PIPELINES.FORM.STAGES.NAME') }}
                    </label>
                    <input
                      v-model="stage.name"
                      type="text"
                      class="h-10 w-full"
                      :placeholder="
                        t('PIPELINE.PIPELINES.FORM.STAGES.NAME_PLACEHOLDER')
                      "
                    />
                  </div>
                  <div class="grid grid-cols-2 gap-4">
                    <div>
                      <label class="block text-label text-n-slate-12 mb-1">
                        {{ t('PIPELINE.PIPELINES.FORM.STAGES.COLOR') }}
                      </label>
                      <StageColorPicker v-model="stage.color" />
                    </div>
                    <div>
                      <label class="block text-label text-n-slate-12 mb-1">
                        {{ t('PIPELINE.PIPELINES.FORM.STAGES.ICON') }}
                      </label>
                      <StageIconPicker v-model="stage.icon" />
                    </div>
                  </div>
                  <div>
                    <label class="block text-label text-n-slate-12 mb-1">
                      {{ t('PIPELINE.PIPELINES.FORM.STAGES.DESCRIPTION') }}
                    </label>
                    <input
                      v-model="stage.description"
                      type="text"
                      class="h-10 w-full"
                      :placeholder="
                        t(
                          'PIPELINE.PIPELINES.FORM.STAGES.DESCRIPTION_PLACEHOLDER'
                        )
                      "
                    />
                  </div>
                  <div
                    v-if="
                      stageSlaEnabled &&
                      !stage.is_won_stage &&
                      !stage.is_lost_stage
                    "
                  >
                    <label class="block text-label text-n-slate-12 mb-1">
                      {{ t('PIPELINE.SLA.STAGE_HOURS_LABEL') }}
                    </label>
                    <input
                      v-model.number="stage.sla_hours"
                      type="number"
                      min="0"
                      step="1"
                      class="h-10 w-full"
                      :placeholder="t('PIPELINE.SLA.STAGE_HOURS_PLACEHOLDER')"
                    />
                    <p class="mt-1.5 text-xs text-n-slate-11">
                      {{ t('PIPELINE.SLA.STAGE_HOURS_HELP') }}
                    </p>
                  </div>
                  <div class="flex justify-end">
                    <button
                      type="button"
                      class="text-sm font-medium text-n-ruby-11 hover:text-n-ruby-11 flex items-center gap-1"
                      @click="removeStage(index)"
                    >
                      <fluent-icon icon="delete" size="14" />
                      {{ t('PIPELINE.PIPELINES.FORM.STAGES.REMOVE') }}
                    </button>
                  </div>
                </div>
              </div>
            </template>
          </draggable>

          <!-- Botão Adicionar Etapa -->
          <button
            type="button"
            class="w-full flex items-center justify-center gap-2 py-3 border-2 border-dashed border-n-weak rounded-lg text-sm font-medium text-n-brand hover:bg-n-alpha-1 transition-colors"
            @click="addNewStage"
          >
            <fluent-icon icon="add" size="16" />
            {{ t('PIPELINE.PIPELINES.FORM.STAGES.ADD') }}
          </button>
        </div>
      </div>
    </div>

    <!-- Footer -->
    <div
      class="mt-6 pt-4 border-t border-n-weak flex justify-end items-center gap-3"
    >
      <Button variant="ghost" color="slate" size="sm" @click="emit('close')">
        {{ t('PIPELINE.CANCEL') }}
      </Button>
      <Button
        variant="solid"
        color="blue"
        size="sm"
        type="submit"
        :is-loading="loading"
        :disabled="
          !formData.name ||
          !stages.length ||
          (showLabelMappings && !labelMappingsValid)
        "
      >
        {{
          t(
            isEditing ? 'PIPELINE.SAVE' : 'PIPELINE.PIPELINES.FORM.CREATE.TITLE'
          )
        }}
      </Button>
    </div>
  </form>
</template>

<style scoped>
/* Ghost card class for vuedraggable library */
.ghost-card {
  opacity: 0.5;
  background: #c8ebfb;
}
</style>
